import apiClient from './apiClient'; // Import the centralized apiClient
import type { ItemPropertiesDto } from '../types/item.types';
//import type { Item } from '../store/slices/itemsSlice'; // Import Item type

/**
 * Fetches items with aggregated quantities across all characters
 * @param game Optional game filter (e.g., 'PoE1', 'PoE2')
 * @param league Optional league filter
 * @returns Promise of Item array with aggregated quantities
 */
export const getAggregatedItems = async (): Promise<Item[]> => {
  try {
    const response = await apiClient.get<Item[]>(`/items/aggregated`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching aggregated items:', error.response?.data || error.message);
    throw error;
  }
};

// Unified Item interface matching backend UnifiedItemDto
export interface Item {
  id: string;
  item_name: string;
  item_type: string;
  properties: ItemPropertiesDto;
  game: string;
  league: string;
  quantity?: number;
  item_slot?: string;
  inventory_type?: string;
  timestamp?: Date;
  character_id?: string;
}

// Keep ApiItemResponse as alias for backward compatibility
export type ApiItemResponse = Item;

// Define a more specific ApiError type if needed, or use a generic one
export interface ApiError {
  message: string;
  statusCode?: number;
  errors?: Record<string, string[]>; // For validation errors
}

/**
 * Fetches items for the authenticated user.
 * The backend /items endpoint should filter items by owner based on JWT.
 */
export const getItems = async (): Promise<Item[]> => {
  try {
    const response = await apiClient.get<Item[]>(`/items`);
    return response.data;
  } catch (error: any) {
    // Log error or transform it as needed
    console.error('Error fetching items:', error.response?.data || error.message);
    // Re-throw the error so it can be caught by the calling function (e.g., in Redux thunk)
    // This allows the thunk to access error.response.data for more detailed error messages.
    throw error;
  }
};

/**
 * Fetches item definitions with optional game and league filters
 */
export const getItemDefinitions = async (): Promise<Item[]> => {
  try {
    const response = await apiClient.get<Item[]>(`/items`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching item definitions:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Unified interface for requesting item counts
 */
export interface ItemCountRequest {
  itemType?: string;
  itemName?: string;
  game: string;
  league: string;
  characterIds?: string[];
}

/**
 * Response interface for item count results
 */
export interface ItemCountResponse {
  character_id: string;
  character_name: string;
  character_hostname?: string;
  count: number;
}

/**
 * Fetches unified item counts for characters based on item name and/or type
 * Supports filtering by specific characters or all characters
 * Always requires game and league to prevent cross-league counting
 */
export const getItemCounts = async (request: ItemCountRequest): Promise<ItemCountResponse[]> => {
  try {
    const response = await apiClient.post<ItemCountResponse[]>('/items/counts', request);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching item counts:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Fetches inventory items for a specific character
 * Returns unified items directly from the updated backend endpoint
 */
export const getCharacterItems = async (characterId: string): Promise<Item[]> => {
  try {
    const response = await apiClient.get<Item[]>(`/items/characters/${characterId}/current-inventory`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching character items:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Gets the history of an item with optional filtering
 */
export interface ItemHistoryResponse {
  id: string;
  quantity: number;
  timestamp: string;
  createdAt: string;
  updatedAt: string;
  character_name?: string;
  item_slot?: string;
  inventory_type?: string;
}

export const getItemHistory = async (itemId: string, characterName?: string): Promise<ItemHistoryResponse[]> => {
  try {
    const response = await apiClient.get<ItemHistoryResponse[]>('/items/history', {
      params: {
        itemId,
        characterName,
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching item history:', error.response?.data || error.message);
    throw error;
  }
};
/**
 * Fetches the quantity progression for a specific item.
 */
export interface ItemQuantityProgressionResponse {
  date: string;
  total_quantity: number;
}

export const getItemQuantityProgression = async (itemId: string, league: string): Promise<ItemQuantityProgressionResponse[]> => {
  try {
    const response = await apiClient.get<ItemQuantityProgressionResponse[]>('/items/quantity-progression', {
      params: {
        itemId,
        league,
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching item quantity progression:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Interface for Divine Orb History response
 * Represents a time series of Divine Orb counts
 */
export interface DivineOrbHistoryDto {
  timestamp: Date;
  quantity: number;
}

/**
 * Fetches the 24-hour history of Divine Orb counts for a specific character
 * Used for plotting history graphs
 */
export const getDivineOrbHistory = async (characterId: string): Promise<DivineOrbHistoryDto[]> => {
  try {
    const url = `/items/characters/${characterId}/divine-orb-history`;
    const response = await apiClient.get<DivineOrbHistoryDto[]>(url);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching divine orb history:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Interface for Divine Orb Per Hour response
 * Contains calculated rate and supporting data
 */
export interface DivineOrbPerHourDto {
  divPerHour: number;
  startTimestamp: Date;
  endTimestamp: Date;
  netGain: number;
  hoursElapsed: number;
}

/**
 * Interface for Divine Orbs Gained in 24 hours response
 * Contains total gains (positive deltas only) and supporting data
 */
export interface DivineOrbsGained24hDto {
  divineOrbsGained: number;
  startTimestamp: Date;
  endTimestamp: Date;
}

/**
 * Interface for Divine Orb Rate History response
 * Contains hourly Div/Hr rates and supporting data
 */
export interface DivineOrbRateHistoryDto {
  timestamp: Date;
  divPerHour: number;
  divineOrbsGained: number;
  startDivineOrbs: number;
  endDivineOrbs: number;
}

/**
 * Fetches the calculated Divine Orbs per hour rate for a specific character
 */
export const getDivineOrbsPerHour = async (characterId: string): Promise<DivineOrbPerHourDto> => {
  try {
    const url = `/items/characters/${characterId}/divine-orb-per-hour`;
    const response = await apiClient.get<DivineOrbPerHourDto>(url);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching divine orbs per hour:', error.response?.data || error.message);
    throw error;
  }
};

// Potentially add other item-related API functions here:
// export const getItemById = async (id: string): Promise<Item> => { ... };
// export const createItem = async (itemData: Partial<Item>): Promise<Item> => { ... };
// export const updateItem = async (id: string, itemData: Partial<Item>): Promise<Item> => { ... };
// export const deleteItem = async (id: string): Promise<void> => { ... };

/**
 * Interface for the bulk Divine Orb data request
 */
export interface BulkDivineOrbDataRequest {
  characterIds: string[];
}

/**
 * Interface for the bulk Divine Orb data response
 */
export interface BulkDivineOrbDataResponse {
  divineOrbHistory: Record<string, DivineOrbHistoryDto[]>;
  divineOrbsPerHour: Record<string, DivineOrbPerHourDto>;
  divineOrbsGained24h: Record<string, DivineOrbsGained24hDto>;
}

/**
 * Fetches Divine Orb and Div/Hr data for multiple characters in a single request
 * This is a performance optimization to reduce API calls when there are many characters.
 *
 * @param characterIds Array of character IDs to fetch data for
 * @returns Object with divineOrbHistory and divineOrbsPerHour, both mapped by character ID
 */
export const getBulkDivineOrbData = async (characterIds: string[]): Promise<BulkDivineOrbDataResponse> => {
  try {
    const url = '/items/characters/bulk/divine-orb-data';
    const response = await apiClient.post<BulkDivineOrbDataResponse>(url, {
      characterIds,
    });

    return response.data;
  } catch (error: any) {
    console.error('Error fetching bulk divine orb data:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Fetches the Div/Hr rate history for a specific character over the last 24 hours
 */
export const getDivineOrbRateHistory = async (characterId: string): Promise<DivineOrbRateHistoryDto[]> => {
  try {
    const url = `/items/characters/${characterId}/divine-orb-rate-history`;
    const response = await apiClient.get<DivineOrbRateHistoryDto[]>(url);

    // Transform timestamp strings to Date objects
    return response.data.map(item => ({
      ...item,
      timestamp: new Date(item.timestamp),
    }));
  } catch (error: any) {
    console.error('Error fetching divine orb rate history:', error.response?.data || error.message);
    throw error;
  }
};
